/**
 * 集团门户专属文案（页面 UI 文案）。业务内容来自 data/*.json，不在这里写死。
 *
 * longnan 分支（2026-10-22）：站群为**中文单语站**——站点字典只存 `zh` 一个语言块，
 * 语言切换器已移除，任何语种都显示中文（App 由共享组件 `ChineseSurface` 锁定）。
 * ⚠️ 不要把中文复制到其它语种槽位；新增文案沿用 `{ zh: {...} }` 的形态。
 * 策略表见 scripts/check-messages.mjs 与 scripts/check-locales.mjs 的 SINGLE_LANGUAGE_SITES。
 */
export const GROUP_MESSAGES: { zh: Record<string, string> } = {
  zh: {
    'nav.publications': '集团刊物',
    'pub.eyebrow': '集团刊物',
    'pub.title': '集团刊物',
    'pub.type.letter': '年度信',
    'pub.type.review': '年度回顾',
    'pub.type.esg': '社会责任',
    'pub.type.facts': '数据一览',
    'pub.notice': '本页内容为集团层面的一般性说明，不构成投资建议或业绩承诺；涉及财务与政策的表述以其原始文件及主管部门最终核定为准。',
    'brand.name': '安迪文化',
    'brand.mark': 'ri-building-2-line',
    'brand.home': '集团门户',
    'nav.about': '集团概况',
    'nav.leadership': '领导致辞',
    'nav.ecosystem': '业务生态',
    'nav.news': '新闻动态',
    'nav.esg': 'ESG 与社会责任',
    'nav.ir': '投资者关系',
    'nav.careers': '加入我们',
    'nav.contact': '联系我们',
    'footer.note': '安迪文化对外信息发布平台，运营总部设于甘肃陇南。图片署名见各页图注。',
    'footer.ecosystem': '业务生态',
    'footer.site': '安迪文化',
    'common.visit': '进入',
    'common.more': '了解更多',
    'common.placeholderVisual': '业务板块配图',
    'common.imageCredit': '图片',

    'home.hero.eyebrow': '安迪文化 · 陇南',
    'home.hero.cta.ecosystem': '探索业务生态',
    'home.hero.cta.about': '了解集团',
    'home.eco.eyebrow': '业务生态',
    'home.eco.title': '六大板块，一键直达',
    'home.eco.lead': '每个业务板块都有独立的官网与栏目，点击即可进入对应子站。',
    'home.about.eyebrow': '集团概况',
    'home.about.title': '运营总部陇南，串联六大业务板块',
    'home.about.more': '查看集团概况',
    'home.facts.founded': '成立时间',
    'home.facts.capital': '注册资本',
    'home.facts.hq': '注册地址',
    'home.facts.lines': '业务板块',
    'home.facts.linesValue': '6 个',
    'home.news.eyebrow': '新闻动态',
    'home.news.title': '最新动态',
    'home.news.all': '查看全部新闻',
    'home.esg.eyebrow': 'ESG 与社会责任',
    'home.esg.title': '守着森林与白龙江，把责任做进日常运营',
    'home.esg.env': '环境',
    'home.esg.env.body': '以森林、峡谷与河流的承载力为边界，控制景区开发强度，推进运营环节节能减排。',
    'home.esg.social': '社会',
    'home.esg.social.body': '把就业与合作机会留在本地，支持非遗传承、乡村民宿与康养从业者培训。',
    'home.esg.gov': '治理',
    'home.esg.gov.body': '各业务板块独立核算、独立负责，集团统一合规、安全与信息披露。',
    'home.esg.cta': '查看 ESG 议题',
    'home.join.title': '加入我们，与六大板块一起成长',
    'home.join.lead': '我们在陇南及甘川陕毗邻地区持续招聘。',
    'home.join.cta': '查看社会招聘岗位',
    'home.join.contact': '联系我们',

    'about.eyebrow': '集团概况',
    'about.intro.title': '关于集团',
    'about.values.title': '使命、愿景与价值观',
    'about.history.title': '发展历程',
    'about.history.lead': '从 2023 年主体成立，到 2025 年《陇南市文旅康养产业高质量发展行动计划》印发后确立主线，集团沿着「大景区带动全域乡村游」的路径形成六大业务板块。',
    'about.structure.title': '组织架构',
    'about.structure.lead': '集团总部统一战略、品牌、财务与合规；各业务板块独立运营、独立负责。',
    'about.structure.root': '安迪文化 · 总部（运营于陇南）',
    'legal.label.name': '法定名称',
    'legal.label.tax': '统一社会信用代码',
    'legal.label.address': '注册地址',

    'eco.eyebrow': '业务生态',
    'eco.title': '六大业务板块',
    'eco.lead': '每个板块以自己的定位对外发声，同时通过客源、线路与场景与集团协同。',
    'eco.keywords': '关键词',

    'skip': '跳到正文',
    'lead.eyebrow': '集团领导',
    'lead.banner': '董事长对集团战略、业务板块协同与长期责任的看法。',
    'lead.team.title': '领导班子',
    'lead.team.lead': '集团领导班子成员。',
    'lead.portrait': '董事长致辞配图',
    'news.eyebrow': '新闻动态',
    'news.title': '集团与业务板块的最新动态',
    'news.lead': '集团层面的决策、各业务板块的进展与社会责任相关的公告，统一在这里发布。',
    'news.filter.all': '全部',
    'news.filter.group': '集团动态',
    'news.filter.subsidiary': '板块动态',
    'news.filter.esg': '社会责任',
    'news.empty': '该分类暂无新闻。',
    'news.readMore': '阅读全文',
    'news.back': '返回新闻列表',
    'news.related': '相关新闻',
    'news.notFound': '未找到该新闻，可能已被移除。',
    'esg.eyebrow': 'ESG 与社会责任',
    'esg.title': '把生态保护与文化传承放进景区运营的日常',
    'esg.lead': '我们把社会责任放在与业务同等的位置。以下为阶段性议题与行动方向，具体指标将随报告分阶段披露。',
    'esg.pillars.title': '三大议题',
    'esg.programs.title': '公益与社区项目',
    'esg.programs.lead': 'ESG 项目持续推进中。',
    'esg.reports.title': '社会责任报告',
    'esg.reports.lead': '报告在编制完成并经董事会审议后发布。',
    'esg.reports.soon': '即将发布',
    'ir.eyebrow': '投资者关系',
    'ir.title': '融资条款、财务摘要与股权结构',
    'ir.lead': '收入按全额法确认；政府补助计入其他收益，不计入营业收入。',
    'ir.round.title': '本轮融资',
    'ir.round.target': '目标融资额',
    'ir.round.equity': '拟出让股权',
    'ir.round.pre': '投前估值',
    'ir.round.post': '投后估值',
    'ir.round.instrument': '融资工具',
    'ir.round.pendingNote': '融资金额、估值与合并财务金额待确认，暂以「待定」列示；比例类指标按现有模型列示。',
    'ir.fin.title': '财务摘要',
    'ir.fin.lead': '{year} 年度，合并口径。',
    'ir.fin.revenue': '营业收入',
    'ir.fin.grossMargin': '毛利率',
    'ir.fin.netProfit': '净利润',
    'ir.fin.netMargin': '净利率',
    'ir.fin.otherIncome': '其他收益（政府补助）',
    'ir.fin.grant': '补助占净利润',
    'ir.fin.note': '政府补助计入其他收益，不计入营业收入；税收优惠冲减所得税费用。',
    'ir.cap.title': '股权结构',
    'ir.cap.lead': '股东结构以集团正式股东名册为准。',
    'ir.history.title': '融资历史',
    'ir.history.round': '轮次',
    'ir.history.date': '时间',
    'ir.history.amount': '金额',
    'ir.history.equity': '出让',
    'ir.history.post': '投后估值',
    'ir.contact.title': '投资者联络',
    'ir.contact.lead': '如需获取尽调材料，请通过以下渠道联系投资者关系部门。',
    'careers.eyebrow': '加入我们',
    'careers.title': '在六大板块中找到你的位置',
    'careers.lead': '我们在陇南招聘，也欢迎熟悉甘川陕毗邻地区与环九寨客源市场的人才与我们同行。',
    'careers.social.title': '社会招聘',
    'careers.social.lead': '以下岗位持续开放，欢迎投递。',
    'careers.apply': '投递简历',
    'careers.campus.title': '校园招聘',
    'careers.form.title': '简历投递',
    'careers.form.success': '感谢投递。本表单尚未接入招聘系统，投递内容不会被保存。',
    'contact.eyebrow': '联系我们',
    'contact.title': '告诉我们你的需求',
    'contact.lead': '景区合作、线路组团、康养旅居、媒体采访与人才合作，都可以从这里开始。',
    'contact.address': '注册地址',
    'contact.channels': '联系渠道',
    'contact.form.title': '留言与咨询',
    'contact.form.success': '感谢留言。本表单尚未接入后台系统，内容不会被保存。',
    'form.required': '带 * 为必填',
    'form.submit': '提交',
    'form.note': '本表单尚未接入后台系统，提交内容不会被保存或发送；如需即时联系，请使用上方联系方式。',
    'form.select': '请选择',
    'form.name': '姓名',
    'form.phone': '手机号',
    'form.email': '邮箱',
    'form.company': '公司或机构',
    'form.role': '意向岗位',
    'form.direction': '咨询方向',
    'form.message': '留言内容',
    'dir.media': '文化传媒',
    'dir.scenic': '景区运营',
    'dir.health': '康养',
    'dir.tech': '智慧旅游',
    'dir.invest': '产业投资',
    'dir.rural': '乡村旅游',
    'dir.ir': '投资者关系',
    'dir.other': '其他',
    'notfound.title': '页面不存在',
    'notfound.body': '该路径未定义。',
  },
};
